import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import InputField from '../../../components/InputField/InputField';
import TextAreaField from '../../../components/TextAreaField/TextAreaField';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import OptionSelector from '../../../components/OptionSelector';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import useFormalExaminationService from '../../../app/services/hooks/useFormalExaminationService';
import { StagesData } from '../../../app/services/hooks/useBrandRegistrationStagesService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';

interface FormalExaminationStepProps {
    brandRegistrationId: number;
    stagesData: StagesData | null;
    isEditable: boolean;
    onUpdate: () => void;
}

interface FormalExaminationData {
    examination_result: 'approved' | 'rejected' | '';
    result_detail: string;
    notification_date: string;
}

const FormalExaminationStep: React.FC<FormalExaminationStepProps> = ({ 
    brandRegistrationId, 
    stagesData,
    isEditable,
    onUpdate 
}) => {
    const history = useHistory();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { 
        getInapiApplicationId,
        getFormalExamination, 
        getInapiAutofillData,
        createFormalExamination, 
        fetchingGetInapiApplicationId,
        fetchingGetFormalExamination,
        fetchingGetInapiAutofillData,
        fetchingCreateFormalExamination 
    } = useFormalExaminationService();
    
    const [formData, setFormData] = useState<FormalExaminationData>({
        examination_result: '',
        result_detail: '',
        notification_date: new Date().toISOString().split('T')[0]
    });

    const [isSaved, setIsSaved] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [inapiApplicationId, setInapiApplicationId] = useState<number | null>(null);
    const [inapiHistory, setInapiHistory] = useState<any[]>([]);
    const [showHistory, setShowHistory] = useState(false);

    // Obtener inapi_application_id al cargar
    useEffect(() => {
        if (brandRegistrationId) {
            getInapiApplicationId(brandRegistrationId, {
                onSuccess: (response: ServiceResponse) => {
                    if (response.data && response.data.inapi_application_id) {
                        setInapiApplicationId(response.data.inapi_application_id);
                        // Cargar datos existentes del examen de forma
                        loadExistingExamination(response.data.inapi_application_id);
                    }
                },
                onError: (response: ServiceResponse) => {
                    console.log('No se pudo obtener inapi_application_id:', response.message);
                    // No mostrar error, simplemente no hay datos previos
                }
            });
        }
    }, [brandRegistrationId]);

    const loadExistingExamination = (inapiAppId: number) => {
        getFormalExamination(inapiAppId, {
            onSuccess: (response: ServiceResponse) => {
                if (response.data && response.data.formal_examination) {
                    const data = response.data.formal_examination;
                    setFormData({
                        examination_result: data.examination_result,
                        result_detail: data.result_detail,
                        notification_date: data.notification_date
                    });
                    setIsSaved(true);
                }
            },
            onError: () => {
                // No hay datos previos, formulario vacío
            }
        });
    };

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setIsSaved(false);
    };

    const handleEdit = () => {
        setIsEditMode(true);
        setIsSaved(false);
    };

    const handleCancelEdit = () => {
        // Recargar datos originales
        if (inapiApplicationId) {
            loadExistingExamination(inapiApplicationId);
        }
        setIsEditMode(false);
    };

    const validateForm = (): boolean => {
        const errors: any = {};

        if (!formData.examination_result) {
            errors.examination_result = ['Debe seleccionar un resultado'];
        }

        if (!formData.result_detail.trim()) {
            errors.result_detail = ['Debe ingresar el detalle del resultado'];
        }

        if (!formData.notification_date) {
            errors.notification_date = ['Debe ingresar la fecha de notificación'];
        }

        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            toast.error('Por favor complete todos los campos requeridos');
            return false;
        }

        return true;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }

        if (!inapiApplicationId) {
            toast.error('No se pudo obtener el ID de la solicitud INAPI. Por favor asegúrate de haber completado la etapa 3.');
            return;
        }

        setErrors({});
        
        const requestData = {
            inapi_application_id: inapiApplicationId,
            examination_result: formData.examination_result,
            result_detail: formData.result_detail,
            notification_date: formData.notification_date
        };

        createFormalExamination(requestData, {
            onSuccess: (response: ServiceResponse) => {
                toast.success(response.message || 'Examen de forma guardado correctamente.');
                setIsSaved(true);
                setIsEditMode(false);
                onUpdate(); // Recargar las etapas
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al guardar el examen de forma.');
            },
            onFieldError: (response: ServiceResponse) => {
                setErrors(response.data);
                toast.error('Por favor corrige los errores en el formulario.');
            }
        });
    };
    
    const handleAutofillFromInapi = () => {
        if (!inapiApplicationId) {
            toast.error('No se puede obtener datos de INAPI sin un ID de aplicación válido.');
            return;
        }
        
        getInapiAutofillData(inapiApplicationId, {
            onSuccess: (response: ServiceResponse) => {
                const data = response.data;
                
                // Siempre mostrar el historial
                if (data.full_history && data.full_history.length > 0) {
                    setInapiHistory(data.full_history);
                    setShowHistory(true);
                }
                
                if (!data.can_autofill) {
                    toast.warning('No se encontró la resolución de aceptación a trámite de marca en INAPI. Revisa el historial y completa manualmente los datos.');
                    return;
                }
                
                // Autocompletar directamente sin confirmación
                setFormData({
                    examination_result: data.examination_result,
                    result_detail: data.result_detail,
                    notification_date: data.notification_date
                });
                
                setIsSaved(false);
                toast.success('✅ Formulario autocompletado con datos de INAPI. Revisa la información y guarda los cambios.');
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al obtener datos de INAPI');
            }
        });
    };

    if (fetchingGetInapiApplicationId || fetchingGetFormalExamination) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="step-container">
            <div className="step-header mb-4">
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <h4 className=" mb-2">
                            <i className="fas fa-file-alt me-2 text-primary"></i>
                            Examen de forma
                        </h4>
                        <p className="text-muted mb-0">Ingrese el resultado del examen de forma de la solicitud INAPI.</p>
                    </div>
                    <div className="d-flex gap-2">
                        {isSaved && !isEditMode && isEditable && (
                            <ButtonGeneral
                                type="button"
                                variant="outline"
                                size="medium"
                                title="Editar"
                                onClick={handleEdit}
                                icon="fas fa-edit"
                            />
                        )}
                        {isEditable && inapiApplicationId && (!isSaved || isEditMode) && (
                            <ButtonGeneral
                                type="button"
                                variant="primary"
                                size="medium"
                                title="Autocompletar con INAPI"
                                onClick={handleAutofillFromInapi}
                                icon="fas fa-magic"
                                loading={fetchingGetInapiAutofillData}
                                disabled={fetchingGetInapiAutofillData}
                            />
                        )}
                    </div>
                </div>
            </div>

            {isSaved && (
                <div className="alert alert-success py-2 mb-4 d-flex align-items-center">
                    <i className="fas fa-check-circle me-2"></i>
                    <small>Datos guardados correctamente.</small>
                </div>
            )}
            
            {/* Historial de INAPI */}
            {showHistory && inapiHistory.length > 0 && (
                <div className="card mb-4" style={{ border: '1px solid #001849' }}>
                    <div 
                        className="card-header d-flex align-items-center justify-content-between"
                        style={{ backgroundColor: '#001849', color: 'white' }}
                    >
                        <h5 className="mb-0 text-white">
                            <i className="fas fa-list me-2"></i>
                            Historial de Trámites INAPI
                        </h5>
                        <button 
                            className="btn btn-sm btn-light"
                            onClick={() => setShowHistory(false)}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead style={{ backgroundColor: '#f8f9fa' }}>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Descripción</th>
                                        <th>Observaciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inapiHistory.map((entry: any, index: number) => (
                                        <tr key={index}>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <i className="fas fa-calendar me-2 text-muted"></i>
                                                {entry.Fecha || 'N/A'}
                                            </td>
                                            <td>
                                                <strong>{entry.EstadoDescripcion || entry.Descripcion || 'N/A'}</strong>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {entry.Observacion || '-'}
                                                </small>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <div className="row g-3">
                {/* Resultado de Solicitud */}
                <div className="col-12 mt-4">
                    <h5 className="border-bottom pb-2 mb-3">Resultado de Solicitud</h5>
                </div>

                <div className="col-12">
                    <label className="fw-bold mb-2">Resultado</label>
                    <p className="text-muted mb-3">Según el resultado obtenido, selecciona una de las opciones.</p>

                    <OptionSelector
                        name="examination_result"
                        options={[
                            {
                                value: 'approved',
                                label: 'Aprobada',
                                description: 'La solicitud fue aprobada en el examen de forma'
                            },
                            {
                                value: 'rejected',
                                label: 'Rechazada',
                                description: 'La solicitud fue rechazada en el examen de forma'
                            }
                        ]}
                        selectedValue={formData.examination_result}
                        onChange={(value) => handleInputChange('examination_result', value)}
                        disabled={!isEditable || (isSaved && !isEditMode)}
                    />
                    
                    {fieldErrorMessage('examination_result') && (
                        <div className="invalid-feedback d-block mt-2">
                            {fieldErrorMessage('examination_result')}
                        </div>
                    )}
                </div>

                {/* Detalle del resultado */}
                <div className="col-12 mt-3">
                    <TextAreaField
                        label="Ingrese el detalle del resultado"
                        value={formData.result_detail}
                        onChange={(value: string) => handleInputChange('result_detail', value)}
                        onFocus={() => onFocusRemove('result_detail')}
                        placeholder="Ingresa detalles del resultado"
                        rows={4}
                        disabled={!isEditable || (isSaved && !isEditMode)}
                        error={fieldErrorMessage('result_detail')}
                    />
                </div>

                {/* Información del número de solicitud INAPI (solo lectura) */}
                {stagesData?.summary.inapi_application_number && (
                    <div className="col-12 mt-3">
                        <div 
                            style={{ 
                                padding: '1rem',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '0.5rem'
                            }}
                        >
                            <label className="text-muted small mb-1 d-block">Número de Solicitud INAPI:</label>
                            <p className="mb-0 fw-bold" style={{ color: '#001849', fontSize: '1.1rem' }}>
                                <i className="fas fa-hashtag me-2"></i>
                                {stagesData.summary.inapi_application_number}
                            </p>
                            <small className="text-muted d-block mt-1">
                                <i className="fas fa-info-circle me-1"></i>
                                Este número fue registrado en la Etapa 3
                            </small>
                        </div>
                    </div>
                )}

                {/* Fecha de notificación del resultado */}
                <div className="col-md-6 mt-3">
                    <InputField
                        label="Fecha de notificación del resultado"
                        type="date"
                        value={formData.notification_date}
                        onChange={(value: string) => handleInputChange('notification_date', value)}
                        onFocus={() => onFocusRemove('notification_date')}
                        disabled={!isEditable || (isSaved && !isEditMode)}
                        error={fieldErrorMessage('notification_date')}
                    />
                </div>
            </div>

            {/* Botones de acción */}
            <div className="d-flex justify-content-end gap-2 mt-4">
                {isSaved && isEditMode && isEditable && (
                    <ButtonGeneral
                        type="button"
                        variant="secondary"
                        size="medium"
                        title="Cancelar"
                        onClick={handleCancelEdit}
                        icon="fas fa-times"
                    />
                )}
                {isEditable && (!isSaved || isEditMode) && (
                    <ButtonGeneral
                        type="button"
                        variant="primary"
                        size="medium"
                        title={fetchingCreateFormalExamination ? 'Guardando...' : 'Guardar'}
                        onClick={handleSubmit}
                        loading={fetchingCreateFormalExamination}
                        disabled={fetchingCreateFormalExamination}
                        icon="fas fa-save"
                        iconPosition="left"
                    />
                )}
            </div>
        </div>
    );
};

export default FormalExaminationStep;
